import React, { Component } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Slide,
    TextField,
    Autocomplete,
    FormGroup,
    IconButton,
    Popover,
    FormControlLabel,
    Checkbox
} from "@mui/material";
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import { TransitionProps } from "@mui/material/transitions";
import InputTextField from "../Reusables/InputTextField";
import { amountFormat } from "../helper";
import PromoCodeServices from "../../Services/PromoCodeService";
import { ShowAlert } from "../ShowAlert";
import PromotionalCode from "../Reusables/CodeGenerator";
import { Messages } from "../Reusables/Utility";
import { DateRange} from '@mui/icons-material';
import { DateRangePicker } from 'react-date-range';
import BigButton from "../BigButton";
import moment from "moment";

// Interface for AddPromoCodeProps
interface AddPromoCodeProps {
    open: boolean;
    accessToken: string;
    onClose?: (state: boolean, action: string) => void;
    refreshData: () => void;
}

// Interface for Influence
type Influence = {
    id: number;
    influence_name: string;
};

//  Interface for minclass state
interface AddPromoCodeState {
    promoCode: string;
    codeValue: string;
    selectedInfluence: Influence | null;
    codeValueError: string;
    newInfluence: string;
    influences: Influence[];
    accessToken: string;
    isCodeValid: boolean;
    minTransactionAmt: string;
    minTransactionAmtError: string;
    selectedDateRange: { startDate: Date; endDate: Date };
    popOverTargetElementForDateRange: HTMLElement | null,
    popOverTargetElementForTempDateRange: HTMLElement | null,
    isRestrictedToFirstTrans: boolean;
}

// Transition component
const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement<any, any> },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Main class for AddPromoCode component
class AddPromoCode extends Component<AddPromoCodeProps, AddPromoCodeState> {
    constructor(props: AddPromoCodeProps) {
        super(props);
        this.state = {
            promoCode: "",
            codeValue: "0.00",
            selectedInfluence: null,
            codeValueError: "",
            newInfluence: "",
            influences: [],
            accessToken: "",
            isCodeValid: true,
            minTransactionAmt: "",
            minTransactionAmtError: "",
            selectedDateRange:
            {
                startDate: new Date(),
                endDate: new Date()
            },
            popOverTargetElementForDateRange: null,
            popOverTargetElementForTempDateRange: null,
            isRestrictedToFirstTrans: false
        };
    }

    // Get access token and influence list for the component
    async componentDidMount() {
        const accesstoken_ = await loadFromLocalStorage('accessToken');

        if (accesstoken_) {
            this.setState({
                accessToken: accesstoken_.toString()
            }, () => {
                this.getInfluenceLists();
            })
        }
        else {
            localStorage.clear();
            window.location.href = '/'
        }
    }

    // Get influence list method
    private getInfluenceLists() {
        PromoCodeServices.getAllInfluences(this.state.accessToken).then(tr => {
            if (tr.ok) {
                tr.json().then(resp => {
                    this.setState({
                        influences: resp || []
                    })
                });
            } else {
                console.log(Messages.Errors.apiError);
                ShowAlert("Alert", Messages.Errors.apiError, "error");
            }
        });
    }

    //Update generated code
    onGenerate = (newCode: string) => {
        console.log("New code:", newCode);
        this.setState({ promoCode: newCode, isCodeValid: false });
    };

    // Handle promo code change & validation
    handleOnChanage = (newCode: string, isValid: boolean) => {
        this.setState({
            promoCode: newCode,
            isCodeValid: isValid, // Only valid if greter than 4 characters
        });
    };

    // HandleOpenPopover function to handle the calender 
    private handleOpenPopover = (
        event: React.MouseEvent<HTMLElement>,
        popoverType: 'dateRange' | 'tempDateRange'
    ) => {
        if (popoverType === 'dateRange') {
            this.setState({ popOverTargetElementForDateRange: event.currentTarget });
        } else {
            this.setState({ popOverTargetElementForTempDateRange: event.currentTarget });
        }
    };

    private handleSelectDateRange = (ranges: any, updateValue: keyof AddPromoCodeState) => {
        const selectedRange = ranges.range1;
        this.setState((prevState) => ({
            ...prevState,
            [updateValue]: selectedRange,
        }));
    };

    private applyDateRange = (
        updateValue: keyof AddPromoCodeState,
        popoverType: 'dateRange' | 'tempDateRange'
    ) => {
        this.setState((prevState) => ({
            ...prevState,
            [updateValue]: this.state[updateValue],
            [popoverType === 'dateRange'
                ? 'popOverTargetElementForDateRange'
                : 'popOverTargetElementForTempDateRange']: null,
        }));
    };


    // Method on save button click
    private handleDialogAction = async (action: string) => {
        if (action === "ok") {
            const { codeValue, minTransactionAmt, selectedInfluence, newInfluence, influences, promoCode, selectedDateRange, isRestrictedToFirstTrans } = this.state;
            let codeValueError = "";
            let minTransactionAmtError = "";

            const sanitizedCodeValue = codeValue ? parseFloat(codeValue.replace(/,/g, "")) : 0;
            const sanitizedMinTransactionAmt = minTransactionAmt ? parseFloat(minTransactionAmt.replace(/,/g, "")) : 0;

            // Validate codeValue
            if (sanitizedCodeValue <= 0) {
                codeValueError = Messages.Errors.couponAmount
            }

            // Validate minTransactionAmt
            if (sanitizedMinTransactionAmt < sanitizedCodeValue) {
                minTransactionAmtError = Messages.Errors.minTransactionAmt;
            }

            // If validation fails, update state and stop execution
            if (codeValueError || minTransactionAmtError) {
                this.setState({ codeValueError, minTransactionAmtError });
                return;
            }

            let matchedInfluence = influences.find(
                (inf) => inf.influence_name.toLowerCase() === newInfluence.toLowerCase()
            );

            const startDate = moment(selectedDateRange.startDate).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
            const endDate = moment(selectedDateRange.endDate).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');

            const postData: any = {
                promoCode: promoCode,
                amount: codeValue,
                minTransactionAmt: sanitizedMinTransactionAmt,
                influenceName: selectedInfluence?.influence_name || matchedInfluence?.influence_name || newInfluence,
                influenceId: selectedInfluence?.id || matchedInfluence?.id || 0,
                applicableFirstTransaction: isRestrictedToFirstTrans,
                startTime: startDate,
                endTime: endDate
            };

            try {
                const rsp = await PromoCodeServices.storePromoCode(this.props.accessToken, postData);
                if (rsp?.ok) {
                    await this.props.refreshData();
                    this.props.onClose?.(true, action);
                    ShowAlert("Success", Messages.Success.promoCodesaved, "success");
                } else {
                    ShowAlert("Alert", Messages.Errors.apiError, "error");
                }
            } catch (error) {
                console.error(Messages.Errors.unexpected, error);
                ShowAlert("Error", Messages.Errors.unexpected, "error");
            }
        } else {
            this.props.onClose?.(false, action);
        }
    };

    render() {
        const { open, onClose } = this.props;
        const { promoCode, isCodeValid, codeValue, selectedInfluence, codeValueError, newInfluence, minTransactionAmt, minTransactionAmtError, selectedDateRange, popOverTargetElementForDateRange, isRestrictedToFirstTrans } = this.state;
        const isSubmitDisabled = !(promoCode && !isCodeValid && codeValue && (selectedInfluence || newInfluence)); // Disable submit button if any of the fields are empty
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" TransitionComponent={Transition} keepMounted>
                <DialogTitle>Add Promo Code</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" marginTop="12px">
                        <PromotionalCode
                            value={this.state.promoCode}
                            placeholder="Enter code or generate"
                            label="Enter code or generate"
                            onGenerate={this.onGenerate}
                            onChange={this.handleOnChanage}
                        />
                        <Box display="flex" flexDirection="column" gap={3} marginTop="12px">
                            <InputTextField
                                value={codeValue}
                                startAdornment="$"
                                inputType="number"
                                onChange={(value) => this.handleLimitChange("codeValue", value)}
                                onBlur={() => this.handleLimitBlur("codeValue")}
                                onFocus={() => this.handleLimitFocus('codeValue')}
                                error={!!codeValueError}
                                helperText={codeValueError}
                                placeholder="Enter Amount"
                            />
                            <InputTextField
                                value={minTransactionAmt}
                                startAdornment="$"
                                inputType="number"
                                onChange={(value) => this.handleLimitChange("minTransactionAmt", value)}
                                onBlur={() => this.handleLimitBlur("minTransactionAmt")}
                                onFocus={() => this.handleLimitFocus('minTransactionAmt')}
                                error={!!minTransactionAmtError}
                                helperText={minTransactionAmtError}
                                placeholder="Min transaction"
                                filedLable="Min transaction"
                            />
                            <Autocomplete
                                sx={{ marginTop: "20px" }}
                                freeSolo
                                options={this.state.influences}
                                getOptionLabel={(option) =>
                                    typeof option === "string" ? option : option.influence_name
                                }
                                value={this.state.selectedInfluence || this.state.newInfluence || ""}
                                inputValue={this.state.newInfluence}
                                onChange={(_, newValue) => {
                                    if (typeof newValue === "string") {
                                        this.setState({ selectedInfluence: null, newInfluence: newValue });
                                    } else {
                                        this.setState({
                                            selectedInfluence: newValue,
                                            newInfluence: newValue?.influence_name || "",
                                        });
                                    }
                                }}
                                onInputChange={(_, newInputValue) => this.handleInputChange(newInputValue)}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Enter influencer"
                                        variant="outlined"
                                        label="Enter influencer"
                                    />
                                )}
                            />
                            <FormGroup>
                                <TextField
                                    variant="outlined"
                                    label="Expiration"
                                    color="secondary"
                                    sx={{ minWidth: "250px", marginTop: "20px" }}
                                    InputProps={{
                                        readOnly: true, // Prevents manual input if only selection is allowed
                                        endAdornment: (
                                            <IconButton onClick={(event) => this.handleOpenPopover(event, 'dateRange')}>
                                                <DateRange />
                                            </IconButton>
                                        ),
                                    }}
                                    value={`Valid From: ${selectedDateRange.startDate.toLocaleDateString()} - Valid To: ${selectedDateRange.endDate.toLocaleDateString()}`}
                                />
                                <Popover
                                    id={'date-range-popover'}
                                    open={Boolean(popOverTargetElementForDateRange)}
                                    anchorEl={popOverTargetElementForDateRange}
                                    onClose={() =>
                                        this.setState({ popOverTargetElementForDateRange: null })
                                    }
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                >
                                    <Box className="popover-calendar">
                                        <DateRangePicker
                                            ranges={[selectedDateRange]}
                                            onChange={(ranges: any) => {
                                                this.handleSelectDateRange(ranges, 'selectedDateRange');
                                            }}
                                            minDate={new Date()}
                                        />
                                        <BigButton
                                            disabled={false}
                                            width="80%"
                                            onClick={() => {
                                                this.applyDateRange('selectedDateRange', 'dateRange');
                                            }}
                                            buttonText="Apply"
                                        />
                                    </Box>
                                </Popover>
                            </FormGroup>
                            <Box style={{ flex: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                          checked={this.state.isRestrictedToFirstTrans} 
                                          onChange={e =>
                                            this.setState({
                                              isRestrictedToFirstTrans: e.target.checked
                                            })
                                          }
                                          color="secondary"
                                          name="isRestrictedToFirstTrans"
                                        />
                                      }
                                    label="Eligible for first transaction"
                                />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => this.handleDialogAction("cancel")}>Cancel</Button>
                    <Button color="secondary" variant="contained"
                        onClick={() => this.handleDialogAction("ok")}
                        disabled={isSubmitDisabled}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    // CLear selected influence
    handleClearSelection = () => {
        this.setState({ selectedInfluence: null });
    };

    // Update new influence value if not in dropdown
    handleInputChange = (inputValue: any) => {
        const sanitizedValue = inputValue
            .replace(/[^a-zA-Z0-9 ]/g, "") // Allow only letters, numbers, and spaces
            .replace(/\b\w/g, (char: any) => char.toUpperCase()) // Convert to title case
            .slice(0, 30); // Limit to 30 characters

        this.setState({ newInfluence: sanitizedValue });
    };

    handleLimitChange = (field: keyof AddPromoCodeState, value: string) => {
        let sanitizedValue = value.replace(/[^0-9.]/g, "");
        const MAX_LIMIT = 999999999;

        // Prevent multiple decimal points
        if ((sanitizedValue.match(/\./g) || []).length > 1) {
            sanitizedValue = sanitizedValue.slice(0, sanitizedValue.lastIndexOf(".") + 1);
        }

        // Ensure value does not exceed max limit
        if (parseFloat(sanitizedValue) > MAX_LIMIT) {
            sanitizedValue = MAX_LIMIT.toString();
        }

        let codeValueError = "";
        let minTransactionAmtError = "";

        switch (field) {
            case "codeValue":
                if (sanitizedValue === "" || sanitizedValue === "0.00" || parseFloat(sanitizedValue) === 0) {
                    codeValueError = Messages.Errors.couponAmount
                }
                break;

            case "minTransactionAmt":
                if (
                    sanitizedValue === "" ||
                    sanitizedValue === "0.00" ||
                    parseFloat(sanitizedValue) === 0 ||
                    (this.state.codeValue && parseFloat(sanitizedValue) < parseFloat(this.state.codeValue))
                ) {
                    minTransactionAmtError = Messages.Errors.minTransactionAmt;
                }
                break;

            default:
                break;
        }

        this.setState((prevState) => ({
            ...prevState,
            [field]: sanitizedValue,
            codeValueError,
            minTransactionAmtError
        }));
    };

    // Handle Amount blur
    handleLimitBlur = (field: keyof AddPromoCodeState) => {
        let value = this.state[field];

        if (typeof value !== "string") {
            value = "0";
        }

        if (!value || isNaN(parseFloat(value))) {
            value = "0";
        }
        const sanitizedValue = value.replace(/,/g, "");
        const updatedAmount = amountFormat(parseFloat(sanitizedValue), "en-US", "USD").replace("$", "");

        this.setState((prevState) => ({
            ...prevState,
            [field]: updatedAmount,
        }));
    };  

    // Handle Amount focus
    handleLimitFocus = (field: keyof AddPromoCodeState) => {
        const value = this.state[field];
        if (typeof value === 'string') {
            const n_ = value.replace(/,/g, "");
            this.setState((prevState) => ({
                ...prevState,
                [field]: n_,
                [`${field}InputType`]: "number"
            }));
        }
    };
}

export default AddPromoCode;
